// src/auth/AuthCallback.jsx
// ─────────────────────────────────────────────────────────────────────────────
// After Google OAuth completes, the backend redirects to:
//   http://localhost:3000/auth/callback?token=ACCESS&refresh=REFRESH&name=...&role=...
//
// This page reads those params, stores the tokens in localStorage
// (same keys your AuthContext already uses), then navigates to "/".
//
// Add this route in App.jsx (inside the Routes, BEFORE RequireAuth):
//   import AuthCallback from './auth/AuthCallback'
//   <Route path="/auth/callback" element={<AuthCallback />} />
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext'

export default function AuthCallback() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { loginWithTokens } = useAuth()  // see note below if this method doesn't exist

  useEffect(() => {
    const params       = new URLSearchParams(location.search)
    const accessToken  = params.get('token')
    const refreshToken = params.get('refresh')

    if (!accessToken) {
      // Something went wrong — back to login with error
      navigate('/login?error=google_auth_failed', { replace: true })
      return
    }

    // Store tokens using the same mechanism as AuthContext
    // If your AuthContext exposes loginWithTokens(), call it.
    // Otherwise fall back to direct localStorage writes matching your existing keys.
    if (typeof loginWithTokens === 'function') {
      loginWithTokens(accessToken, refreshToken)
    } else {
      localStorage.setItem('fslp:access',  accessToken)
      if (refreshToken) localStorage.setItem('fslp:refresh', refreshToken)
    }

    // Clean the URL and go to the app
    // Use replace so back-button doesn't return to /auth/callback
    navigate('/dashboard', { replace: true })
  }, [])

  // Brief loading screen while the effect fires
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0F1A2E',
      gap: 20,
      fontFamily: "'Inter', sans-serif",
    }}>
      <div style={{
        width: 36, height: 36,
        border: '3px solid rgba(255,255,255,.15)',
        borderTop: '3px solid #EA580C',
        borderRadius: '50%',
        animation: 'spin .7s linear infinite',
      }}/>
      <div style={{ color: 'rgba(255,255,255,.5)', fontSize: 14, fontWeight: 600 }}>
        Signing you in…
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}